import * as React from "react";
import { useMemo } from "react";

import { Combobox, type OpcionDeCombobox } from "@/components/ui/combobox";
import { useComunas } from "@/hooks/useComunas";
import type { Comuna } from "@/lib/comunas";

/**
 * El resto de las props va al disparador. Es lo que deja que `FormControl`
 * inyecte `id` y `aria-describedby` por su `Slot` y el `<FormLabel>` encuentre
 * el control que etiqueta.
 */
interface SelectorDeComunaProps
  extends Omit<React.ComponentProps<"button">, "value" | "onChange" | "type"> {
  /** El código CUT elegido, o `null` mientras no haya ninguno. */
  valor: string | null;
  onValorChange: (codigo: string | null) => void;
  /**
   * El nombre que el informe tiene CONGELADO. Se usa solo para no dejar el
   * campo en blanco en un informe anterior al maestro: esos roles tienen la
   * comuna escrita a mano y ningún código, y mostrar el placeholder haría
   * parecer que el dato se perdió.
   */
  nombreCongelado?: string | null;
  invalido?: boolean;
}

interface SelectorDeComunaBaseProps extends SelectorDeComunaProps {
  comunas: Comuna[] | undefined;
  cargando: boolean;
  conError: boolean;
}

/**
 * El selector en sí, SIN decidir de dónde salen las comunas.
 *
 * Está separado porque el maestro se sirve por DOS puertas: `/api/comunas`
 * dentro del grupo autenticado, para el equipo, y `/api/portal/{token}/comunas`
 * para el cliente, que no tiene sesión. Es el mismo registro legal y tiene que
 * elegirse igual en las dos pantallas — con el mismo placeholder, la misma
 * búsqueda por código y la misma pista de provincia y región.
 *
 * La alternativa era un segundo selector copiado para el portal, y ahí las dos
 * copias empiezan iguales y terminan distintas: alguien mejora la búsqueda en
 * una y el cliente externo se queda con la vieja.
 */
export function SelectorDeComunaBase({
  comunas,
  cargando,
  conError,
  valor,
  onValorChange,
  nombreCongelado,
  invalido,
  ...resto
}: SelectorDeComunaBaseProps) {
  const opciones = useMemo<OpcionDeCombobox[]>(
    () =>
      (comunas ?? []).map((comuna) => ({
        valor: comuna.codigo,
        etiqueta: comuna.nombre,
        // El CUT a la vista y buscable. Es el número con el que la comuna
        // aparece en los documentos del SII, así que quien está transcribiendo
        // un rol lo tiene delante y le sirve para las dos cosas: llegar a la
        // comuna tecleándolo, y confirmar que la que eligió es la del papel.
        codigo: comuna.codigo,
        // Provincia y región desambiguan lo que el nombre solo no alcanza:
        // "Los Lagos" es una comuna de la región de Los Ríos, y sin la línea
        // de abajo se elige a ciegas. Además entran en la búsqueda, así que se
        // puede llegar a una comuna tecleando su región.
        pista: `${comuna.provincia} · ${comuna.region}`,
      })),
    [comunas],
  );

  // Mientras el maestro viaja, el disparador anuncia que está cargando en vez
  // de ofrecer una lista vacía —que se lee como "no hay comunas"— y queda
  // deshabilitado para que nadie lo abra sobre la nada.
  if (cargando) {
    return (
      <Combobox
        opciones={[]}
        valor={null}
        onValorChange={() => {}}
        placeholder="Cargando comunas…"
        disabled
        {...resto}
      />
    );
  }

  if (conError) {
    return (
      <Combobox
        opciones={[]}
        valor={null}
        onValorChange={() => {}}
        placeholder="No se pudieron cargar las comunas"
        disabled
        invalido
        {...resto}
      />
    );
  }

  return (
    <Combobox
      opciones={opciones}
      valor={valor}
      onValorChange={onValorChange}
      // El nombre congelado como placeholder es el caso del informe viejo: se
      // ve lo que el informe dice hoy, y elegir del maestro lo reemplaza.
      placeholder={nombreCongelado || "Selecciona una comuna"}
      // El placeholder NOMBRA las dos formas de buscar: que se pueda tipear el
      // código no sirve de nada si nadie lo intenta porque el campo solo
      // prometía el nombre.
      placeholderBusqueda="Buscar comuna o código…"
      mensajeVacio="Ninguna comuna coincide."
      invalido={invalido}
      {...resto}
    />
  );
}

/**
 * Elige la comuna del registro oficial (RF-E1 §8.2), para las pantallas del
 * equipo.
 *
 * Reemplaza al campo de texto libre que había antes. El motivo no es comodidad:
 * escrita a mano, la misma comuna entraba de cinco formas distintas ("La
 * Calera", "Calera", "la calera"), y rol + comuna es la clave con la que el SII
 * reconoce la parcela y con la que RF-E3 busca una retasación. Una clave que se
 * escribe a mano no sirve para cruzar nada.
 *
 * Lo que viaja al backend es el CÓDIGO. El nombre no: el servidor lo congela
 * desde el maestro, para que el informe no pueda decir un nombre que
 * contradice a su código.
 *
 * El portal del cliente NO usa este envoltorio sino {@link SelectorDeComunaBase}
 * directo: `useComunas` pega a la ruta autenticada, y del otro lado no hay
 * sesión.
 */
export function SelectorDeComuna(props: SelectorDeComunaProps) {
  const comunas = useComunas();

  return (
    <SelectorDeComunaBase
      comunas={comunas.data}
      cargando={comunas.isPending}
      conError={comunas.isError}
      {...props}
    />
  );
}
